/**
 * Regel aanmaken/bewerken (`/rules/create`, `/rules/edit/:id`) — gemeten aan
 * oud. Beide routes gebruiken hetzelfde formulier; alleen de kop en het
 * doel-endpoint verschillen.
 *
 * Velden zoals oud: Rule Name, Description, Category, Scope, Priority (met
 * de hint "Higher = evaluated first"), Action, Conditions (JSON) met het
 * voorbeeld dat oud toont, Action Parameters (JSON), Model Persona ID
 * (optional) en Platform (optional).
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorNote, Loading, Panel } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'

interface Rule {
  id: number
  name: string
  description: string | null
  category: string
  scope: string
  priority: number
  action: string
  conditions: Record<string, unknown> | null
  action_params: Record<string, unknown> | null
  model_persona_id: number | null
  platform: string | null
}

const CATEGORIES = ['escalation', 'safety', 'sales', 'tone', 'routing']
const SCOPES = ['global', 'model', 'platform']
const ACTIONS = ['notify', 'takeover', 'block', 'tag', 'pause_ai']
const PLATFORMS = ['', 'fancentro', 'f2f']

export default function RuleEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { run, busy } = useActions()
  const isNew = !id

  const q = useApi<Rule>(isNew ? '' : `/api/admin/rules/${id}`, [id])
  const [f, setF] = useState<Record<string, string>>({
    name: '', description: '', category: 'escalation', scope: 'global',
    priority: '0', action: 'notify', conditions: '', action_params: '',
    model_persona_id: '', platform: '',
  })
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    const r = q.data
    if (!r) return
    setF({
      name: r.name ?? '',
      description: r.description ?? '',
      category: r.category ?? 'escalation',
      scope: r.scope ?? 'global',
      priority: String(r.priority ?? 0),
      action: r.action ?? 'notify',
      conditions: r.conditions ? JSON.stringify(r.conditions, null, 2) : '',
      action_params: r.action_params ? JSON.stringify(r.action_params, null, 2) : '',
      model_persona_id: r.model_persona_id ? String(r.model_persona_id) : '',
      platform: r.platform ?? '',
    })
  }, [q.data])

  if (!isNew && q.loading && !q.data) return <Loading />
  if (!isNew && q.error) return <ErrorNote error={q.error} />

  const set = (k: string, v: string) => setF({ ...f, [k]: v })

  function body() {
    const parse = (t: string, veld: string) => {
      if (!t.trim()) return null
      try { return JSON.parse(t) } catch (e) {
        throw new Error(`${veld}: ${e instanceof Error ? e.message : 'ongeldige JSON'}`)
      }
    }
    return {
      name: f.name,
      description: f.description || null,
      category: f.category,
      scope: f.scope,
      priority: Number(f.priority) || 0,
      action: f.action,
      conditions: parse(f.conditions, 'Conditions'),
      action_params: parse(f.action_params, 'Action Parameters'),
      model_persona_id: f.model_persona_id ? Number(f.model_persona_id) : null,
      platform: f.platform || null,
    }
  }

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={() => navigate('/rules')}>
          Rules
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        <button
          className="btn primary sm"
          type="button"
          disabled={!f.name.trim() || busy === 'Save rule'}
          onClick={() => {
            let payload
            try { payload = body() } catch (e) {
              setJsonError(e instanceof Error ? e.message : String(e)); return
            }
            setJsonError(null)
            run({
              label: 'Save rule',
              request: isNew
                ? mut.post('/api/admin/rules', payload)
                : mut.patch(`/api/admin/rules/${id}`, payload),
              onDone: () => navigate('/rules'),
              successText: isNew ? 'Rule created.' : 'Rule saved.',
            })
          }}
        >
          Save
        </button>
      </div>

      <Panel title={isNew ? 'Create Rule' : 'Edit Rule'}>
        <div className="field">
          <label>Rule Name</label>
          <input className="inp" value={f.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea className="inp" rows={2} value={f.description}
                    onChange={(e) => set('description', e.target.value)} />
        </div>
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>Category</label>
            <select className="inp" value={f.category}
                    onChange={(e) => set('category', e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Scope</label>
            <select className="inp" value={f.scope} onChange={(e) => set('scope', e.target.value)}>
              {SCOPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Priority</label>
            <input className="inp" type="number" value={f.priority}
                   onChange={(e) => set('priority', e.target.value)} />
            <span className="hint">Higher = evaluated first (takes precedence)</span>
          </div>
        </div>
        <div className="field">
          <label>Action</label>
          <select className="inp" value={f.action} onChange={(e) => set('action', e.target.value)}>
            {ACTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Conditions (JSON)</label>
          <textarea className="inp mono" rows={4} value={f.conditions}
                    placeholder={'e.g. {"fan_messages_count": {"gte": 5}}'}
                    onChange={(e) => { set('conditions', e.target.value); setJsonError(null) }} />
        </div>
        <div className="field">
          <label>Action Parameters (JSON)</label>
          <textarea className="inp mono" rows={4} value={f.action_params}
                    onChange={(e) => { set('action_params', e.target.value); setJsonError(null) }} />
        </div>
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>Model Persona ID (optional)</label>
            <input className="inp" type="number" value={f.model_persona_id}
                   onChange={(e) => set('model_persona_id', e.target.value)} />
          </div>
          <div className="field">
            <label>Platform (optional)</label>
            <select className="inp" value={f.platform}
                    onChange={(e) => set('platform', e.target.value)}>
              {PLATFORMS.map((c) => <option key={c} value={c}>{c || '—'}</option>)}
            </select>
          </div>
        </div>
        {jsonError ? (
          <div className="note" style={{ borderColor: 'rgba(255,107,107,.4)' }}>
            <span style={{ color: 'var(--bad)' }}>Ongeldige JSON:</span> {jsonError}
          </div>
        ) : null}
      </Panel>
    </>
  )
}
